<template>
  <div class="d-flex flex-column view-wrapper">
    
    <v-card class="elevation-1 rounded-0 mb-1 flex-shrink-0" color="primary">
      <v-card-text class="pa-2 d-flex justify-space-between align-center">
        <div>
          <div class="text-subtitle-1 font-weight-bold white--text">
            Resumo de Vendas por Grupo
          </div>
          <div class="caption white--text">
            {{ periodoFormatado }}
          </div>
        </div>
        
        <v-chip small color="white" outlined>
          {{ items.length }} Grupos
        </v-chip>
      </v-card-text>
    </v-card>

    <div class="content-container flex-grow-1 elevation-1 white" style="position: relative; min-height: 0;">
      
      <div v-if="loading" class="d-flex justify-center align-center fill-height">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <div v-else-if="items.length === 0" class="d-flex justify-center align-center fill-height grey--text">
        <div class="text-center">
          <v-icon size="48" color="grey lighten-2">mdi-chart-pie</v-icon>
          <p class="mt-2">Nenhuma venda encontrada no período.</p>
        </div>
      </div>

      <simplebar
        v-else
        :auto-hide="false"
        class="table-scroll-container"
        style="height: 100%;"
      >
        <table class="table-custom">
          <colgroup>
            <col style="width: 60px;" />
            <col style="width: auto;" />
            <col style="width: 25%;" />
            <col style="width: 18%;" />
          </colgroup>
          <thead class="table-header sticky-header">
            <tr>
              <th class="text-center">ID</th>
              <th class="text-left">Grupo</th>
              <th class="text-right">Valor</th>
              <th class="text-right pr-3">% Part.</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in items"
              :key="item.idgrupo"
              class="table-row"
              :class="{ 'linha-alternada': index % 2 !== 0 }"
            >
              <td class="text-center grey--text text--darken-1">
                {{ item.idgrupo }}
              </td>
              
              <td class="text-left font-weight-medium text-truncate" :title="item.descricao">
                {{ item.descricao }}
              </td>
              
              <td class="text-right">
                {{ formatarValor(item.valor) }}
              </td>
              
              <td class="text-right pr-3 font-weight-bold" :class="getCorPercentual(item.percentual)">
                {{ item.percentual.toFixed(1) }}%
              </td>
            </tr>
          </tbody>
        </table>
      </simplebar>
    </div>

    <v-card class="elevation-4 rounded-0 flex-shrink-0 grey lighten-4">
      <v-card-text class="pa-3">
        <v-row no-gutters justify="end" align="center">
          <v-col cols="auto" class="mr-4">
            <span class="caption grey--text text--darken-2 text-uppercase">Total Geral:</span>
          </v-col>
          <v-col cols="auto">
            <div class="text-h6 font-weight-bold green--text text--darken-3">
              {{ formatarValor(subtotal) }}
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

  </div>
</template>

<script>
import simplebar from 'simplebar-vue';
import 'simplebar-vue/dist/simplebar.min.css';
import db from '@/plugins/dexie';

export default {
  name: 'ViewResumoVendasGrupo',

  components: {
    simplebar,
  },

  data() {
    return {
      loading: false,
      items: [],
      periodoFormatado: '',
      idVendedor: 0,
    };
  },

  computed: {
    subtotal() {
      return this.items.reduce((acc, item) => acc + item.valor, 0);
    },
  },

  created() {
    this.carregarResumoPorGrupo();
  },

  methods: {
    formatarValor(valor) {
      if (!valor) return '0,00';
      return valor.toLocaleString('pt-BR', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    },

    getCorPercentual(valor) {
      if (valor > 0) return 'green--text text--darken-2';
      if (valor < 0) return 'red--text text--darken-2';
      return 'grey--text';
    },

    async carregarResumoPorGrupo() {
      this.loading = true;
      this.items = [];

      try {
        const parametro = await db.parametro.toCollection().first();
        
        const idVendedorLogado = parametro && parametro.idvendedorlogado 
          ? parseInt(parametro.idvendedorlogado, 10) 
          : 0;

        if (idVendedorLogado === 0) {
          console.warn("Nenhum vendedor logado encontrado na tabela de parâmetros.");
          this.loading = false;
          return; 
        }
        this.idVendedor = idVendedorLogado;

        let inicioPeriodo, fimPeriodo;
        const paramInicio = this.$route.query['created-from'] || this.$route.query['createdFrom'];
        const paramFim = this.$route.query['created-before'] || this.$route.query['createdBefore'];

        if (paramInicio && paramFim) {
          const [anoIni, mesIni, diaIni] = paramInicio.split('-');
          inicioPeriodo = new Date(anoIni, mesIni - 1, diaIni, 0, 0, 0);

          const [anoFim, mesFim, diaFim] = paramFim.split('-');
          fimPeriodo = new Date(anoFim, mesFim - 1, diaFim, 23, 59, 59);
        } else if (paramInicio) {
          const [anoIni, mesIni, diaIni] = paramInicio.split('-');
          inicioPeriodo = new Date(anoIni, mesIni - 1, diaIni, 0, 0, 0);
          fimPeriodo = new Date(anoIni, mesIni - 1, diaIni, 23, 59, 59);
        } else {
          const agora = new Date();
          inicioPeriodo = new Date(agora.getFullYear(), agora.getMonth(), 1, 0, 0, 0);
          fimPeriodo = new Date(agora.getFullYear(), agora.getMonth() + 1, 0, 23, 59, 59);
        }

        const optData = { day: '2-digit', month: '2-digit', year: 'numeric' };
        if (paramInicio && paramFim) {
          this.periodoFormatado = `${inicioPeriodo.toLocaleDateString('pt-BR', optData)} até ${fimPeriodo.toLocaleDateString('pt-BR', optData)}`;
        } else if (paramInicio) {
          this.periodoFormatado = `${inicioPeriodo.toLocaleDateString('pt-BR', optData)}`;
        } else {
          const agora = new Date();
          this.periodoFormatado = agora.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
        }

        const dataInicioStr = inicioPeriodo.toISOString();
        const dataFimStr = fimPeriodo.toISOString();

        const collection = db.venda
            .where('data')
            .between(dataInicioStr, dataFimStr, true, true)
            .and(venda => venda.idvendedor === idVendedorLogado && venda.cancelado !== 'S');

        const vendasFiltradas = await collection.toArray();

        if (vendasFiltradas.length === 0) {
          this.loading = false;
          return;
        }

        const idsProdutos = new Set();
        vendasFiltradas.forEach(v => {
          if (v.idcortamanhofinal) {
            idsProdutos.add(parseInt(v.idcortamanhofinal)); 
          }
        });

        const arrayIds = Array.from(idsProdutos);

        const produtos = await db.produto
          .where('idcortamanhofinal')
          .anyOf(arrayIds)
          .toArray();

        const mapaProdutoGrupo = {};
        
        produtos.forEach(p => {
          const idProd = parseInt(p.idcortamanhofinal);
          const idG = p.idgrupo || 999; 
          const descG = p.descricaogrupo || (p.idgrupo ? `GRUPO ${p.idgrupo}` : 'SEM GRUPO DEFINIDO');

          mapaProdutoGrupo[idProd] = {
            id: idG,
            descricao: descG
          };
        });

        const agrupamento = {};
        let totalGeral = 0;

        vendasFiltradas.forEach(venda => {
          const idProd = parseInt(venda.idcortamanhofinal);
          const valor = parseFloat(venda.valor) || 0;
          
          const dadosGrupo = mapaProdutoGrupo[idProd] || { 
            id: 0, 
            descricao: `PRODUTO NÃO IDENTIFICADO (ID: ${idProd})` 
          };

          if (!agrupamento[dadosGrupo.id]) {
            agrupamento[dadosGrupo.id] = {
              idgrupo: dadosGrupo.id,
              descricao: dadosGrupo.descricao,
              valor: 0
            };
          }

          agrupamento[dadosGrupo.id].valor += valor;
          totalGeral += valor;
        });

        this.items = Object.values(agrupamento).map(grupo => {
          return {
            idgrupo: grupo.idgrupo,
            descricao: grupo.descricao,
            valor: grupo.valor,
            percentual: totalGeral > 0 ? (grupo.valor / totalGeral) * 100 : 0
          };
        });

        this.items.sort((a, b) => b.valor - a.valor);

      } catch (error) {
        console.error("Erro ao carregar resumo por grupo:", error);
      } finally {
        this.loading = false;
      }
    },
  },

  watch: {
    '$route.query': {
      deep: true,
      handler() {
        this.carregarResumoPorGrupo();
      },
    },
  },
};
</script>

<style scoped>
.view-wrapper {
  height: calc(100vh - 100px);
  background-color: white;
}

.table-custom {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.table-header th {
  background-color: #f5f5f5;
  color: #616161;
  padding: 10px 4px;
  font-size: 0.75rem;
  text-transform: uppercase;
  border-bottom: 2px solid #e0e0e0;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 2;
  box-shadow: 0 2px 2px -1px rgba(0,0,0,0.1);
}

.table-row td {
  padding: 8px 6px;
  border-bottom: 1px solid #eeeeee;
  font-size: 0.85rem;
  color: #424242;
}

.linha-alternada {
  background-color: #fafafa;
}

.table-scroll-container ::v-deep .simplebar-scrollbar::before {
  background: rgba(76, 111, 175, 0.4) !important;
}
</style>